import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavActions } from '../components/NavBar/NavActions';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { TimelineEvent } from '../components/subscription-detail/TimelineEvent';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Button } from '../components/SubscriptionAppButton';
import { Checkbox } from '../components/Checkbox';
import { Input } from '../components/SubscriptionAppInput';
import { Settings, Award, Gift, User, ShoppingBag, MapPin, ChevronRight, X, ChevronDown, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
// Mock Data
const pointsActivityData = [{
  name: 'Mon',
  earned: 4000,
  redeemed: 2400,
  expired: 400
}, {
  name: 'Tue',
  earned: 3000,
  redeemed: 1398,
  expired: 200
}, {
  name: 'Wed',
  earned: 2000,
  redeemed: 9800,
  expired: 200
}, {
  name: 'Thu',
  earned: 2780,
  redeemed: 3908,
  expired: 300
}, {
  name: 'Fri',
  earned: 1890,
  redeemed: 4800,
  expired: 100
}, {
  name: 'Sat',
  earned: 2390,
  redeemed: 3800,
  expired: 200
}, {
  name: 'Sun',
  earned: 3490,
  redeemed: 4300,
  expired: 100
}];
const tierDistributionData = [{
  name: 'Enthusiast',
  value: 62,
  color: '#0EA5E9'
}, {
  name: 'Connoisseur',
  value: 24,
  color: '#6F42C1'
}, {
  name: 'Sommelier',
  value: 11,
  color: '#F59E0B'
}, {
  name: 'Vintner',
  value: 3,
  color: '#10B981'
} // Ocean Success
];
const recentActivity = [{
  id: 1,
  type: 'earned',
  user: 'Sarah Johnson',
  title: 'Points Earned',
  description: 'Sarah Johnson earned 150 points from Order #4521',
  time: '2 hours ago',
  icon: ShoppingBag,
  iconColor: '#10B981'
}, {
  id: 2,
  type: 'redeemed',
  user: 'James Chen',
  title: 'Reward Redeemed',
  description: 'James Chen redeemed $10 Off reward',
  time: '5 hours ago',
  icon: Gift,
  iconColor: '#0EA5E9'
}, {
  id: 3,
  type: 'tier',
  user: 'Emma Wilson',
  title: 'Tier Upgrade',
  description: 'Emma Wilson upgraded to Sommelier tier',
  time: '1 day ago',
  icon: Award,
  iconColor: '#6F42C1'
}, {
  id: 4,
  type: 'earned',
  user: 'David Park',
  title: 'Points Earned',
  description: 'David Park earned 100 points from cellar door visit',
  time: '1 day ago',
  icon: MapPin,
  iconColor: '#10B981'
}, {
  id: 5,
  type: 'earned',
  user: 'Sophie Miller',
  title: 'Points Earned',
  description: 'Sophie Miller earned 50 points from referral',
  time: '2 days ago',
  icon: User,
  iconColor: '#10B981'
}];
const topRewards = [{
  name: 'Free Shipping',
  count: 342
}, {
  name: '$10 Off Next Order',
  count: 218
}, {
  name: 'Free Tasting',
  count: 156
}, {
  name: 'Free Rosé Bottle',
  count: 89
}, {
  name: 'Gold for 1 Month',
  count: 12
}];
export function LoyaltyOverviewPage() {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  return <div className="bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-6676">
      {/* Header */}
      <div className="mb-6" data-id="element-6677">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-6678">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-6679">
            Loyalty & Rewards
          </h1>
          <div className="flex-1" data-id="element-6680" />
          <Button variant="primary" iconLeft={<Settings className="w-[18px] h-[18px]" data-id="element-6682" />} onClick={() => setIsSettingsOpen(true)} data-id="element-6681">
            Program Settings
          </Button>
          <NavActions data-id="element-6683" />
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-id="element-6684">
        <StatCard label="Active Members" value="1,247" trend={<TrendIndicator value={12} direction="up" label="vs last month" data-id="element-6686" />} data-id="element-6685" />
        <StatCard label="Points Issued" value="485,200" secondaryValue="this month" data-id="element-6687" />
        <StatCard label="Redemption Rate" value="34%" trend={<TrendIndicator value={3} direction="up" data-id="element-6689" />} data-id="element-6688" />
        <StatCard label="Points Liability" value="$4,850" secondaryValue="unredeemed value" data-id="element-6690" />
      </div>

      {/* Row 2: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-6691">
        {/* Points Activity Chart */}
        <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm p-6" data-id="element-6692">
          <div className="flex items-center justify-between mb-6" data-id="element-6693">
            <h3 className="text-lg font-semibold text-ocean-text-primary" data-id="element-6694">
              Points Activity
            </h3>
            <div className="flex bg-ocean-gray-100 rounded-lg p-1" data-id="element-6695">
              {['7d', '30d', '90d', '1y'].map((period, idx) => <button key={period} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${idx === 0 ? 'bg-white text-ocean-text-primary shadow-sm' : 'text-ocean-text-secondary hover:text-ocean-text-primary'}`} data-id="element-6696">
                  {period}
                </button>)}
            </div>
          </div>
          <div className="h-[300px] w-full" data-id="element-6697">
            <ResponsiveContainer width="100%" height="100%" data-id="element-6698">
              <AreaChart data={pointsActivityData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-6699">
                <defs data-id="element-6700">
                  <linearGradient id="colorEarned" x1="0" y1="0" x2="0" y2="1" data-id="element-6701">
                    <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.1} data-id="element-6702" />
                    <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} data-id="element-6703" />
                  </linearGradient>
                  <linearGradient id="colorRedeemed" x1="0" y1="0" x2="0" y2="1" data-id="element-6704">
                    <stop offset="5%" stopColor="#6F42C1" stopOpacity={0.1} data-id="element-6705" />
                    <stop offset="95%" stopColor="#6F42C1" stopOpacity={0} data-id="element-6706" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-6707" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#94A3B8',
                fontSize: 12
              }} dy={10} data-id="element-6708" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#94A3B8',
                fontSize: 12
              }} data-id="element-6709" />
                <Tooltip contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} data-id="element-6710" />
                <Area type="monotone" dataKey="earned" stroke="#0EA5E9" fillOpacity={1} fill="url(#colorEarned)" strokeWidth={2} name="Earned" data-id="element-6711" />
                <Area type="monotone" dataKey="redeemed" stroke="#6F42C1" fillOpacity={1} fill="url(#colorRedeemed)" strokeWidth={2} name="Redeemed" data-id="element-6712" />
                <Area type="monotone" dataKey="expired" stroke="#94A3B8" fill="transparent" strokeWidth={2} strokeDasharray="5 5" name="Expired" data-id="element-6713" />
                <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{
                top: -10,
                right: 0
              }} data-id="element-6714" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tier Distribution Chart */}
        <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm p-6" data-id="element-6715">
          <h3 className="text-lg font-semibold text-ocean-text-primary mb-6" data-id="element-6716">
            Tier Distribution
          </h3>
          <div className="h-[300px] w-full flex items-center justify-center" data-id="element-6717">
            <ResponsiveContainer width="100%" height="100%" data-id="element-6718">
              <PieChart data-id="element-6719">
                <Pie data={tierDistributionData} cx="50%" cy="50%" innerRadius={80} outerRadius={110} paddingAngle={5} dataKey="value" data-id="element-6720">
                  {tierDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-6721" />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} data-id="element-6722" />
                <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={(value, entry: any) => <span className="text-sm text-ocean-text-secondary ml-1" data-id="element-6724">
                      {value} ({entry.payload.value}%)
                    </span>} data-id="element-6723" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 3: Activity & Rewards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-6725">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm flex flex-col h-full" data-id="element-6726">
          <div className="p-6 border-b border-ocean-border-subtle" data-id="element-6727">
            <h3 className="text-lg font-semibold text-ocean-text-primary" data-id="element-6728">
              Recent Activity
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[400px] p-6" data-id="element-6729">
            <ol className="list-none space-y-0" role="list" aria-label="Recent loyalty activity" data-id="element-6730">
              {recentActivity.map((activity, index) => <TimelineEvent key={activity.id} title={activity.title} description={activity.description} date={activity.time} icon={activity.icon} iconColor={activity.iconColor} isLast={index === recentActivity.length - 1} data-id="element-6731" />)}
            </ol>
          </div>
          <div className="p-4 border-t border-ocean-border-subtle bg-ocean-gray-50 rounded-b-lg" data-id="element-6732">
            <Button variant="ghost" size="sm" onClick={() => navigate('/loyalty/members')} iconRight={<ChevronRight className="w-4 h-4" data-id="element-6734" />} data-id="element-6733">
              View All Members
            </Button>
          </div>
        </div>

        {/* Top Rewards */}
        <div className="bg-white rounded-lg border border-ocean-border-subtle shadow-sm flex flex-col h-full" data-id="element-6735">
          <div className="p-6 border-b border-ocean-border-subtle" data-id="element-6736">
            <h3 className="text-lg font-semibold text-ocean-text-primary" data-id="element-6737">
              Top Rewards
            </h3>
          </div>
          <div className="flex-1 p-6" data-id="element-6738">
            <div className="space-y-3" data-id="element-6739">
              {topRewards.map((reward, index) => {
              const maxCount = topRewards[0].count;
              const barPercent = Math.round(reward.count / maxCount * 100);
              const rankColors = ['bg-[#F59E0B] text-white', 'bg-[#94A3B8] text-white', 'bg-[#CD7F32] text-white'];
              const rankClass = index < 3 ? rankColors[index] : 'bg-ocean-gray-100 text-ocean-text-secondary';
              return <div key={index} className="group relative rounded-lg border border-ocean-border-subtle hover:border-ocean-primary/30 hover:shadow-sm transition-all p-3" data-id="element-6740">
                    <div className="flex items-center gap-3 mb-2" data-id="element-6741">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${rankClass}`} data-id="element-6742">
                        {index + 1}
                      </div>
                      <span className="text-sm font-medium text-ocean-text-primary flex-1 truncate" data-id="element-6743">
                        {reward.name}
                      </span>
                      <span className="text-sm font-semibold text-ocean-text-primary tabular-nums" data-id="element-6744">
                        {reward.count.toLocaleString()}
                      </span>
                    </div>
                    <div className="ml-9" data-id="element-6745">
                      <div className="h-1.5 bg-ocean-gray-100 rounded-full overflow-hidden" data-id="element-6746">
                        <div className="h-full rounded-full bg-gradient-to-r from-ocean-primary to-[#38BDF8] transition-all duration-500" style={{
                      width: `${barPercent}%`
                    }} data-id="element-6747" />
                      </div>
                      <div className="flex items-center justify-between mt-1" data-id="element-6748">
                        <span className="text-[11px] text-ocean-text-muted" data-id="element-6749">
                          redemptions
                        </span>
                        <span className="text-[11px] text-ocean-text-muted" data-id="element-6750">
                          {barPercent}% of top
                        </span>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
          <div className="p-4 border-t border-ocean-border-subtle bg-ocean-gray-50 rounded-b-lg" data-id="element-6751">
            <Button variant="ghost" size="sm" onClick={() => navigate('/loyalty/rewards')} iconRight={<ChevronRight className="w-4 h-4" data-id="element-6753" />} data-id="element-6752">
              Manage Rewards
            </Button>
          </div>
        </div>
      </div>

      {/* Program Settings Drawer Overlay */}
      {isSettingsOpen && <div className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300" onClick={() => setIsSettingsOpen(false)} data-id="element-6754" />}

      {/* Program Settings Drawer Panel */}
      <div className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${isSettingsOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-6755">
        {/* Drawer Header */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between flex-shrink-0" data-id="element-6756">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6757">
            Program Settings
          </h2>
          <IconButton icon={<X className="w-5 h-5" data-id="element-6759" />} label="Close settings" variant="ghost" onClick={() => setIsSettingsOpen(false)} data-id="element-6758" />
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-6760">
          {/* Section 1: Program Basics */}
          <section data-id="element-6761">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-6762">
              Program Basics
            </h3>
            <div className="space-y-4" data-id="element-6763">
              <Input type="text" label="Program Name" defaultValue="Acme Wine Rewards" data-id="element-6764" />

              <div data-id="element-6765">
                <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-6766">
                  Program Status
                </label>
                <div className="flex gap-4" data-id="element-6767">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-6768">
                    <input type="radio" name="status" defaultChecked className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-6769" />
                    <span className="text-sm text-[#0F172A]" data-id="element-6770">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-6771">
                    <input type="radio" name="status" className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-6772" />
                    <span className="text-sm text-[#0F172A]" data-id="element-6773">Paused</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-6774">
                    <input type="radio" name="status" className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-6775" />
                    <span className="text-sm text-[#0F172A]" data-id="element-6776">Test Mode</span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Audience Mode */}
          <section data-id="element-6777">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-6778">
              Audience Mode
            </h3>
            <div className="space-y-3" data-id="element-6779">
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC] transition-colors" data-id="element-6780">
                <input type="radio" name="audience" defaultChecked className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-6781" />
                <div data-id="element-6782">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-6783">
                    All Customers
                  </div>
                  <div className="text-xs text-[#64748B]" data-id="element-6784">
                    Both subscribers and non-subscribers earn and redeem
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC] transition-colors" data-id="element-6785">
                <input type="radio" name="audience" className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-6786" />
                <div data-id="element-6787">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-6788">
                    Non-Subscribers Only
                  </div>
                  <div className="text-xs text-[#64748B]" data-id="element-6789">
                    Only non-subscribers participate
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC] transition-colors" data-id="element-6790">
                <input type="radio" name="audience" className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-6791" />
                <div data-id="element-6792">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-6793">
                    Custom (Tag-Based)
                  </div>
                  <div className="text-xs text-[#64748B]" data-id="element-6794">
                    Limit to customers with specific tags
                  </div>
                </div>
              </label>
            </div>
          </section>

          {/* Section 3: Points Economy */}
          <section data-id="element-6795">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-6796">
              Points Economy
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-6797">
              <div data-id="element-6798">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6799">
                  Earning Rate
                </label>
                <div className="relative" data-id="element-6800">
                  <input type="number" defaultValue="10" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-6801" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-6802">
                    pts/$1
                  </span>
                </div>
              </div>
              <div data-id="element-6803">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6804">
                  Redemption Value
                </label>
                <div className="relative" data-id="element-6805">
                  <input type="number" defaultValue="0.01" step="0.01" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-6806" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-6807">
                    $/pt
                  </span>
                </div>
              </div>
              <div data-id="element-6808">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6809">
                  Minimum Redemption
                </label>
                <div className="relative" data-id="element-6810">
                  <input type="number" defaultValue="500" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-6811" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-6812">
                    points
                  </span>
                </div>
              </div>
              <div data-id="element-6813">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6814">
                  Rounding
                </label>
                <div className="relative" data-id="element-6815">
                  <select className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-6816">
                    <option data-id="element-6817">Nearest</option>
                    <option data-id="element-6818">Floor</option>
                    <option data-id="element-6819">Ceiling</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-6820" />
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Expiration */}
          <section data-id="element-6821">
            <div className="flex items-center justify-between mb-4" data-id="element-6822">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6823">
                Expiration
              </h3>
              <div className="flex items-center gap-2" data-id="element-6824">
                <span className="text-sm text-[#0F172A]" data-id="element-6825">Points Expire</span>
                <button className="w-11 h-6 bg-[#0EA5E9] rounded-full relative transition-colors" data-id="element-6826">
                  <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm translate-x-5" data-id="element-6827" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-6828">
              <div data-id="element-6829">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6830">
                  Expiration Period
                </label>
                <div className="relative" data-id="element-6831">
                  <input type="number" defaultValue="12" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-6832" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-6833">
                    months
                  </span>
                </div>
              </div>
              <div data-id="element-6834">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6835">
                  Expiry Warning
                </label>
                <div className="relative" data-id="element-6836">
                  <input type="number" defaultValue="30" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-6837" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-6838">
                    days
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Klaviyo Integration */}
          <section data-id="element-6839">
            <div className="flex items-center justify-between mb-4" data-id="element-6840">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6841">
                Klaviyo Integration
              </h3>
              <button className="w-11 h-6 bg-[#0EA5E9] rounded-full relative transition-colors" data-id="element-6842">
                <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm translate-x-5" data-id="element-6843" />
              </button>
            </div>
            <div className="space-y-3" data-id="element-6844">
              {['Points earned', 'Points redeemed', 'Tier changed', 'Reward redeemed', 'Points expiring', 'Birthday bonus'].map(event => <div key={event} className="block" data-id="element-6845">
                  <Checkbox checked={true} onChange={() => {}} label={event} data-id="element-6846" />
                </div>)}
            </div>
          </section>

          {/* Section 6: Danger Zone */}
          <section className="border border-[#FECACA] rounded-lg p-4 bg-[#FEF2F2]" data-id="element-6847">
            <h3 className="text-sm font-semibold text-[#DC2626] uppercase tracking-wider mb-4" data-id="element-6848">
              Danger Zone
            </h3>
            <div className="space-y-3" data-id="element-6849">
              <Button variant="secondary" size="sm" onClick={() => setShowResetConfirm(true)} className="w-full border-[#FCA5A5] text-[#DC2626] hover:bg-[#FEE2E2]" data-id="element-6850">
                Reset All Points
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)} className="w-full" data-id="element-6851">
                Delete Program
              </Button>
            </div>
          </section>
        </div>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-end gap-3 flex-shrink-0" data-id="element-6852">
          <Button variant="ghost" size="md" onClick={() => setIsSettingsOpen(false)} data-id="element-6853">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={() => setIsSettingsOpen(false)} data-id="element-6854">
            Save Settings
          </Button>
        </div>
      </div>

      {/* Reset All Points Confirmation Modal */}
      {showResetConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowResetConfirm(false)} data-id="element-6855" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-6856">
            <div className="bg-white rounded-lg shadow-xl w-[420px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6857">
              <div className="p-6" data-id="element-6858">
                <div className="flex items-start gap-4" data-id="element-6859">
                  <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0" data-id="element-6860">
                    <AlertTriangle className="w-5 h-5 text-[#F59E0B]" data-id="element-6861" />
                  </div>
                  <div className="flex-1" data-id="element-6862">
                    <h3 className="text-base font-semibold text-[#0F172A] mb-1" data-id="element-6863">
                      Reset All Points
                    </h3>
                    <p className="text-sm text-[#475569] leading-relaxed" data-id="element-6864">
                      This will set all member point balances to zero. This
                      action cannot be undone. Members will lose any unredeemed
                      points.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-6865">
                <Button variant="ghost" size="sm" onClick={() => setShowResetConfirm(false)} data-id="element-6866">
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={() => {
              setShowResetConfirm(false);
            }} data-id="element-6867">
                  Reset All Points
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Delete Program Confirmation Modal */}
      {showDeleteConfirm && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowDeleteConfirm(false)} data-id="element-6868" />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-6869">
            <div className="bg-white rounded-lg shadow-xl w-[420px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6870">
              <div className="p-6" data-id="element-6871">
                <div className="flex items-start gap-4" data-id="element-6872">
                  <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center flex-shrink-0" data-id="element-6873">
                    <AlertTriangle className="w-5 h-5 text-[#EF4444]" data-id="element-6874" />
                  </div>
                  <div className="flex-1" data-id="element-6875">
                    <h3 className="text-base font-semibold text-[#0F172A] mb-1" data-id="element-6876">
                      Delete Loyalty Program
                    </h3>
                    <p className="text-sm text-[#475569] leading-relaxed" data-id="element-6877">
                      This will permanently delete the entire loyalty program,
                      including all member data, points, tiers, and rewards.
                      This action is irreversible.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-3" data-id="element-6878">
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)} data-id="element-6879">
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={() => {
              setShowDeleteConfirm(false);
              setIsSettingsOpen(false);
            }} data-id="element-6880">
                  Delete Program
                </Button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}